import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';
import { ChatMessagesProps, Message, User } from '@shared-types';
import { MessageBox, Character, speakMessage } from '@components';

interface ChatMessagesExtendedProps extends ChatMessagesProps {
  setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>;
  onEditMessage: (
    messageId: string,
    editedMessage: string,
    type: 'user' | 'char'
  ) => void;
  firstMessage: string;
  onGenerateImage: (messageId: string) => void;
}

export function ChatMessages({
  previous_message,
  isGenerating = false,
  user,
  char,
  onRegenerate,
  onRemove,
  onEditMessage,
  setIsGenerating,
  firstMessage,
  onGenerateImage,
}: ChatMessagesExtendedProps) {
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingMessageType, setEditingMessageType] = useState<
    'user' | 'char' | null
  >(null);
  const [editedMessage, setEditedMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [previous_message, firstMessage]);

  const handleSpeak = async (text: string) => {
    setIsGenerating(true);
    try {
      await speakMessage(text, char.gender as string, () => {
        setIsGenerating(false);
      });
    } catch {
      setIsGenerating(false);
    }
  };

  const handleEditClick = (
    messageId: string,
    currentMessage: string,
    type: 'user' | 'char'
  ) => {
    setEditingMessageId(messageId);
    setEditingMessageType(type);
    setEditedMessage(currentMessage);
  };

  const handleSaveEdit = async (
    messageId: string,
    editedMessage: string,
    type: 'user' | 'char'
  ) => {
    if (onEditMessage) {
      setIsLoading(true);
      await onEditMessage(messageId, editedMessage, type);
      setIsLoading(false);
    }
    setEditingMessageId(null);
    setEditingMessageType(null);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingMessageType(null);
  };

  const messages = firstMessage
    ? [
        {
          id: 'first-message',
          type: 'char',
          text: firstMessage,
          name: char.name,
          profile_image: char.profile_image,
        },
        ...previous_message,
      ]
    : previous_message;

  return (
    <Box className="space-y-4 p-4 max-w-2xl mx-auto">
      {messages.map((msg: Message, index: number) => {
        const isLastMessage = index === messages.length - 1;
        return (
          <MessageBox
            key={`${msg.type}-${msg.id ?? `temp-${index}`}`}
            msg={msg}
            index={index}
            isGenerating={isGenerating}
            isLastMessage={isLastMessage}
            user={user as User}
            char={char as Character}
            onRegenerate={onRegenerate}
            onRemove={onRemove}
            onEditMessage={onEditMessage}
            handleSpeak={handleSpeak}
            setIsGenerating={setIsGenerating}
            editingMessageId={editingMessageId}
            editingMessageType={editingMessageType}
            editedMessage={editedMessage}
            isLoading={isLoading}
            onEditClick={handleEditClick}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            setEditedMessage={setEditedMessage}
            onGenerateImage={onGenerateImage}
          />
        );
      })}
      <div ref={bottomRef} />
    </Box>
  );
}
